import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { DynamicControl } from './DynamicControl';
import PropTypes from 'prop-types';
import { DynamicFieldData } from './DynamicControlTypes';
import FormComponent from './components/Form.component';
import { yupResolver } from '@hookform/resolvers/yup';
import { Grid } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { schemaValidation } from './conf/FormSchema.conf';

const isFieldVisible = (field, values) => {
  const rule = field?.visibleWhen;
  if (!rule) return true;

  if (typeof rule === 'function') return !!rule(values);
  if (typeof rule !== 'object') return true;

  const value = values?.[rule.field];
  if (Array.isArray(rule.in)) return rule.in.includes(value);
  if ('equals' in rule) return value === rule.equals;
  if ('notEquals' in rule) return value !== rule.notEquals;

  return true;
};

const getFieldRequired = (field, values) => {
  const rule = field?.requiredWhen;
  if (!rule) return !!field?.required;
  if (typeof rule === 'function') return !!rule(values);
  return !!field?.required;
};

export const Form = ({
  fields,
  schema,
  defaultValues,
  onSubmit = () => {},
  children,
  actions,
}) => {
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValues,
  });
  const watchValues = useWatch({ control: methods.control });

  const visibleFields = useMemo(() => {
    return fields
      .filter((field) => isFieldVisible(field, watchValues))
      .map((field) => ({
        ...field,
        required: getFieldRequired(field, watchValues),
      }));
  }, [fields, watchValues]);

  useEffect(() => {
    fields?.map((item) => {
      const parse = schemaValidation.safeParse(item);
      if (!parse?.success) {
        console.error(parse.error);
      }
      return item;
    });
  }, [fields]);

  useEffect(() => {
    methods.reset(defaultValues);
    // eslint-disable-next-line
  }, [defaultValues]);

  return (
    <FormProvider {...methods}>
      <FormComponent onSubmit={methods.handleSubmit(onSubmit)}>
        <Grid container spacing={1}>
          {actions && (
            <Grid item xs={12}>
              {actions}
            </Grid>
          )}
          {visibleFields.map(({ xs, sm, md, lg, ...d }, i) => (
            <Grid item xs={xs} sm={sm} md={md} lg={lg} key={i}>
              <DynamicControl {...d} />
            </Grid>
          ))}
          <Grid item xs={12}>
            {children}
          </Grid>
        </Grid>
        {process.env.REACT_APP_FORM_DEV_MODE === true && (
          <div>
            <pre>{JSON.stringify(methods.getValues(), null, 1)}</pre>
          </div>
        )}
      </FormComponent>
    </FormProvider>
  );
};

Form.propTypes = {
  fields: PropTypes.arrayOf(DynamicFieldData),
  schema: PropTypes.object,
};
